<!--
Just so the translations get picked up by the i18n extractor
$t('#evaluation level: Beyond expectations')
$t('#evaluation level: Achieving expectations')
$t('#evaluation level: Continue practicing')
$t('#evaluation level: Instructions needed')
-->

<template>
	<Drawer :title="$t('Evaluate')" initialWidth="xl" @requestUnmount="hideEditor">

		<template #header>
			<div class="font-semibold text-md w-full">
				{{ $t('Evaluate') }}
				<div class="flex gap-2 w-full items-center justify-center">
					<PupilSymbol :pupil="pupil" class="w-12" />
					<span class="font-bold text-lg">{{ pupil.getFullName() }}</span>
				</div>
			</div>
		</template>

		<template #default="{ hide }">

			<form @submit.prevent="save" class="p-6 flex flex-col gap-10">

				<div class="flex items-start gap-3">
					<MapPinIcon class="w-6 h-6 shrink-0" />
					<div>
						<GoalTitle :goal="goal" :showPath="true" />
					</div>
				</div>

				<div v-if="!readOnly">
					<EvaluationLevelPicker
						class="mb-4"
						:modelValue="newLevel"
						:highlightedLevel="mostRecentEvaluation?.evaluationLevel ?? null"
						@update:modelValue="setEvaluation"
					/>

					<div class="flex gap-3 h-0 transition-all overflow-hidden" :class="[newLevel? 'h-24' : '']">
						<div v-if="usersStore.getMe().avatar"  class="w-14 h-14 shrink-0 rounded-xl bg-base-100 place-content-center text-center">
							<img :src="usersStore.getMe().avatar" referrerpolicy="no-referrer" class="w-full h-full rounded-xl object-cover" />
						</div>
						<textarea class="textarea textarea-bordered h-24 w-full" ref="eval-note" :disabled="newLevel? false : true" :placeholder="$t('Note')" v-model="comment"></textarea>
					</div>
				</div>

				<div v-if="evaluationsStore.loading" class="flex justify-center">
					<span class="loading loading-spinner loading-sm"></span>
				</div>

				<div v-else class="obs-and-eval-list flex flex-col gap-8">

					<hr>

					<div v-for="(item, index) in evaluationsAndObservations" class="item-fade-in" :style="{ animationDelay: `${index * 0.1}s` }">

						<!-- EVALUATION -->
						<div v-if="item.type === 'evaluation'" class="evaluation flex gap-4">
							<div class="w-12 h-12 shrink-0">
								<UserAvatar v-if="item.evaluation?.author" :user="item.evaluation.author" :title="item.evaluation.author?.getNameIdentifier()" />
							</div>
							<div class="grow flex flex-col gap-1">
								<div class="flex items-center justify-between gap-4">
									<span class="text-xs">
										<!-- {{ item.evaluation.author?.getNameIdentifier() }}
										-  -->
										{{ localDateTime(item.evaluation.date) }}
									</span>

									<Dropdown position="top-end">
										<template v-if="item.evaluation?.author?.isMe()" #trigger>
											<button type="button" class="btn btn-sm btn-ghost btn-circle group hover:bg-danger">
												<TrashIcon class="w-4 h-4 text-base-content-light group-hover:text-white"></TrashIcon>
											</button>
										</template>
										<template #content="dialogActions">
											<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-80"  @cancel="dialogActions.close" @confirm="deleteEvaluation(item.evaluation)">
												<span class="text-danger">{{ $t('Are you sure?') }}</span>
											</DropdownDialogConfirm>
										</template>
									</Dropdown>

								</div>
								<div class="flex gap-2">
									<div class="text-sm bg-base-100 rounded-lg py-3 px-3 flex gap-2 items-start">
										<span
											class="inline-block w-6 h-6 rounded-full shrink-0 m-1 border border-white"
											:style="{ backgroundColor: item.evaluation.evaluationLevel.color }"
											:title="item.evaluation.evaluationLevel.nameTranslationKey ? $t(item.evaluation.evaluationLevel.nameTranslationKey) : item.evaluation.evaluationLevel.name"
										></span>
										<span class="pt-1 font-semibold" v-if="item.evaluation.comment" >“{{ item.evaluation.comment }}”</span>
									</div>

								</div>
							</div>
						</div>

						<!-- OBSERVATION -->
						<div v-if="item.type === 'observation'" class="observation flex gap-4">
							<div class="w-12 h-12 shrink-0">
								<UserAvatar v-if="item.observation?.author" :user="item.observation.author" :title="item.observation.author?.getNameIdentifier()" />
							</div>

							<div class="flex flex-col gap-1">
								<div class="text-xs flex items-center gap-2">

									<!-- {{ item.observation.author?.getNameIdentifier() }}
									- -->
									{{ localDateTime(item.observation.date) }}
									<span class="inline-flex items-center gap-0.5 bg-info text-info-content px-2 py-0.5 rounded">
										<EyeIcon class="w-3 h-3 inline-block mr-1" />
										{{ $t('Observation') }}
									</span>
								</div>
								<div class="mt-1 text-sm bg-base-100 rounded-lg py-3 px-3 flex flex-col gap-2">
									<div class="flex gap-2">
										<template v-for="file in item.observation.files" :key="file.id">
											<div v-if="file.isImage()" class="w-20 h-20 p-1 bg-base rounded relative">
												<FilePreview :file="file" data-fancybox="gallery" class="block w-full h-full" />
											</div>
										</template>
									</div>
									<p v-if="item.observation.note" class="font-semibold">“{{ item.observation.note }}”</p>
								</div>
							</div>

						</div>

					</div>
				</div>

				<div class="drawer-action-bar" :class="[inUpdateMode? 'justify-between' : 'justify-end']">
					<div v-if="readOnly">
						<button type="button" class="btn btn-ghost" @click="hide">
							{{ $t('Close') }}
						</button>
					</div>
					<div v-else class="flex justify-end gap-1">
						<button type="button" class="btn btn-ghost" @click="hide" :class="[saving || deleting? 'hidden':'']">
							{{ $t('Cancel') }}
						</button>
						<button type="submit" class="btn" :class="[!canSave? 'btn-disabled':'btn-primary']">
							<span v-show="saving" class="loading loading-spinner"></span>
							{{ $t('Save') }}
						</button>
					</div>
				</div>

			</form>

		</template>
	</Drawer>
</template>

<style>
.item-fade-in {
	@apply opacity-0;
	animation: fade-in 0.5s forwards;
}
@keyframes fade-in {
	0% { opacity: 0; }
	100% { opacity: 1; }
}
</style>

<script lang="ts">
import Drawer from "@/components/ui/Drawer.v2.vue";
import { Goal } from "@/models/Goal.model";
import { Pupil } from "@/models/Pupil.model";
import { ChevronUpIcon } from "@heroicons/vue/24/outline";
import { UserCircleIcon, MapPinIcon, PencilIcon, TrashIcon, EyeIcon } from "@heroicons/vue/24/outline";
import GoalTitle from "@/components/GoalTitle.vue";
import EvaluationLevelPicker from "@/components/EvaluationLevelPicker.vue";
import { mapStores } from "pinia";
import { useEvaluationLevelsStore } from "@/stores/EvaluationLevels.store";
import { EvaluationLevel } from "@/models/EvaluationLevel.model";
import { useEvaluationsStore } from "@/stores/Evaluations.store";
import { EvaluationFilterDef } from "@/stores/filters/EvaluationFilterDef";
import { DateTime } from "luxon";
import { Observation } from "@/models/Observation.model";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import { Evaluation } from "@/models/Evaluation.model";
import { forkedObservationsStore } from "@/stores/Observations.store";
import { ObservationFilterDef } from "@/stores/filters/ObservationFilterDef";
import FilePreview from "@/components/FilePreview.vue";
import { useUsersStore } from "@/stores/Users.store";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import UserAvatar from "@/components/UserAvatar.vue";


interface EvaluationOrObservation {
	type: 'evaluation' | 'observation';
	evaluation?: Evaluation;
	observation?: Observation;
	date: Date;
}

export default {
	emits: ['requestClose'],

	components: {
		Drawer,
		ChevronUpIcon, UserCircleIcon, MapPinIcon, PencilIcon, TrashIcon, EyeIcon,
		GoalTitle,
		EvaluationLevelPicker,
		Dropdown,
		DropdownDialogConfirm,
		FilePreview,
		PupilSymbol,
		UserAvatar,
	},

	props: {
		pupil: {
			type: Pupil,
			required: true,
		},
		goal: {
			type: Goal,
			required: true,
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			inUpdateMode: false,
			newLevel: null as EvaluationLevel | null,
			saving: false,
			deleting: false,
			comment: '',
			mostRecentEvaluation: null as Evaluation | null,
		}
	},

	computed: {

		...mapStores(
			useEvaluationLevelsStore,
			useEvaluationsStore,
			useUsersStore,

			forkedObservationsStore('evaluationObservations'),
		),

		canSave() {
			return this.newLevel && !this.saving && !this.deleting;
		},

		localDateTime() {
			return (date: Date) => {
				return DateTime.fromJSDate(date).toLocaleString({
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
				});
			};
		},

		evaluationsAndObservations(): EvaluationOrObservation[] {

			const out: EvaluationOrObservation[] = [];

			this.evaluationsStore.items.forEach((evaluation) => {
				out.push({
					type: 'evaluation',
					evaluation: evaluation,
					date: evaluation.date,
				});
			});

			this.evaluationObservationsStore.items.forEach((observation: Observation) => {
				out.push({
					type: 'observation',
					observation: observation,
					date: observation.date,
				});
			});

			// sort by date (descending)
			out.sort((a, b) => {
				if (a.date > b.date) {
					return -1;
				} else if (a.date < b.date) {
					return 1;
				}
				return 0;
			});

			// get the most recent evaluation
			this.mostRecentEvaluation = out.find((item) => item.type === 'evaluation')?.evaluation || null;

			return out;

		}

	},

	async mounted() {

		this.evaluationLevelsStore.loadOnce();
		await this.usersStore.loadOnce();

		// load evaluations for specific pupil.
		const evaluationFilter = new EvaluationFilterDef();
		evaluationFilter.pupilId = this.pupil.id;
		evaluationFilter.goalId = this.goal.id;

		this.evaluationsStore.applyFilter(evaluationFilter);

		// load observations for specific pupil.
		const observationFilter = new ObservationFilterDef();
		observationFilter.pupilId = this.pupil.id;
		observationFilter.goalId = this.goal.id;

		this.evaluationObservationsStore.applyFilter(observationFilter, false);

	},

	methods: {
		hideEditor () {
			this.$emit('requestClose');
		},

		setEvaluation(level: EvaluationLevel) {

			this.newLevel = level;
			// set focus to note textarea, but with a short delay
			setTimeout(() => {
				this.$refs['eval-note'].focus();
			}, 100);

		},

		save() {

			if (!this.newLevel) {
				return;
			}

			this.saving = true;

			const evaluation = this.evaluationsStore.new();

			evaluation.evaluationLevel = this.newLevel;
			evaluation.pupilId = this.pupil.id;
			evaluation.goalId = this.goal.id;
			evaluation.comment = this.comment;
			evaluation.goalCollectionId = this.goal.goalCollectionId;

			this.evaluationsStore.save(evaluation).then(() => {
				this.saving = false;
				this.hideEditor();

				// Also add to the loaded goal, if it was not added automatically (ie when the goal was not loaded via the store)
				if (!this.goal.getEvaluations(this.pupil).find(e => e.id === evaluation.id)) {
					this.goal.addEvaluation(this.pupil.id, evaluation);
				}

				this.$notify({
					// title:
					text: this.$t('Evaluation saved'),
					type: 'success',
				});
			});

		},

		deleteEvaluation(evaluation: Evaluation) {

			this.deleting = true;

			this.evaluationsStore.delete(evaluation).then(() => {
				this.deleting = false;
			});

		},
	}
}
</script>
